import { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Download, MessageSquare, Star, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useMyBookings } from "@/hooks/useApiData";
import { guestApi } from "@/lib/api";

const TABS = [
  { id: "upcoming", label: "Upcoming", filter: (b) => b.status === "confirmed" },
  { id: "past", label: "Past", filter: (b) => b.status === "completed" },
  { id: "all", label: "All", filter: () => true },
];

export const Bookings = () => {
  const [tab, setTab] = useState("upcoming");
  const { items: BOOKINGS, loading, error } = useMyBookings();
  const list = BOOKINGS.filter(TABS.find((t) => t.id === tab).filter);

  return (
    <div data-testid="bookings-root">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">Account · Bookings</p>
      <h1 className="mt-3 font-display text-4xl font-medium leading-[0.95] tracking-tight md:mt-4 md:text-6xl">Your <span className="italic">stays</span>.</h1>

      <div className="mt-10 flex gap-2 border-b border-foreground/10 md:mt-14">
        {TABS.map((t) => (
          <button key={t.id} type="button" data-testid={`bookings-tab-${t.id}`} onClick={() => setTab(t.id)} className={`relative -mb-px px-4 py-3 text-sm font-medium transition-colors ${tab === t.id ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {t.label}
            <span className="ml-2 text-xs text-muted-foreground">{loading ? "—" : BOOKINGS.filter(t.filter).length}</span>
            {tab === t.id && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary" />}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-6">
        {loading ? (
          <BookingsSkeleton />
        ) : error ? (
          <div className="border border-dashed border-foreground/20 px-6 py-16 text-center md:py-24">
            <p className="font-display text-3xl md:text-4xl">We couldn’t load your bookings.</p>
            <p className="mt-3 text-sm text-muted-foreground">Refresh the page and try again.</p>
          </div>
        ) : list.length === 0 ? (
          <EmptyState />
        ) : (
          list.map((b) => <BookingRow key={b.id} booking={b} />)
        )}
      </div>
    </div>
  );
};

const BookingRow = ({ booking }) => {
  const isUpcoming = booking.status === "confirmed";

  const downloadInvoice = async () => {
    if (!booking.transaction?.id) {
      toast.info("Invoice will be available shortly.");
      return;
    }
    try {
      const res = await guestApi.invoice(booking.transaction.id);
      const url = res?.unwrapped?.url;
      if (!url) throw new Error("Invoice URL missing");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.apiMessage || err?.response?.data?.message || "Could not open invoice.");
    }
  };

  return (
    <article data-testid={`booking-${booking.id}`} className="grid grid-cols-1 border border-foreground/10 bg-card md:grid-cols-[220px_1fr_auto]">
      <Link to={`/properties/${booking.property.id}`} className="block">
        <img src={booking.property.images[0]} alt="" className="h-48 w-full object-cover md:h-full" />
      </Link>

      <div className="p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge status={booking.status} />
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Ref {booking.reference || booking.id}</span>
        </div>
        <h3 className="mt-3 font-display text-2xl font-medium tracking-tight">{booking.property.name}</h3>
        <p className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">{booking.property.location} · Hosted by {booking.property.host.name}</p>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {fmtRange(booking.checkIn, booking.checkOut)}</span>
          <span>{booking.nights} nights · {booking.guests} guests</span>
          <span className="text-foreground"><span className="font-display text-base">${booking.total}</span> total</span>
        </div>
      </div>

      <div className="flex flex-col items-stretch gap-2 border-t border-foreground/10 p-6 md:border-l md:border-t-0 md:p-8">
        {isUpcoming && (
          <Link to={`/account/bookings/${booking.id}/messages`} className="relative inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs font-medium text-background">
            <MessageSquare size={13} /> Message host
            {booking.unreadMessages > 0 && <span className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">{booking.unreadMessages}</span>}
          </Link>
        )}
        <button type="button" disabled={!booking.invoiceReady} onClick={downloadInvoice} className="inline-flex items-center justify-center gap-2 rounded-full border border-foreground/20 px-5 py-2.5 text-xs font-medium transition-colors hover:border-foreground disabled:opacity-40">
          <Download size={13} /> {booking.invoiceReady ? "Invoice" : "Pending"}
        </button>
        {booking.canReview && (
          <Link to="/account/reviews" className="inline-flex items-center justify-center gap-2 rounded-full bg-primary/10 px-5 py-2.5 text-xs font-medium text-primary transition-colors hover:bg-primary/15"><Star size={13} /> Leave review</Link>
        )}
        <Link to={`/properties/${booking.property.id}`} className="inline-flex items-center justify-center gap-1 pt-1 text-xs text-muted-foreground hover:text-foreground">View property <ArrowRight size={12} /></Link>
      </div>
    </article>
  );
};

const BookingsSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    {Array.from({ length: 3 }).map((_, index) => (
      <div key={index} className="grid overflow-hidden border border-foreground/10 md:grid-cols-[220px_1fr_auto]">
        <div className="h-48 bg-muted md:h-52" />
        <div className="p-6 md:p-8"><div className="h-4 w-24 rounded bg-muted" /><div className="mt-5 h-7 w-2/3 rounded bg-muted" /><div className="mt-3 h-3 w-1/2 rounded bg-muted" /><div className="mt-5 h-4 w-3/4 rounded bg-muted" /></div>
        <div className="border-t border-foreground/10 p-6 md:w-44 md:border-l md:border-t-0"><div className="h-9 rounded-full bg-muted" /><div className="mt-3 h-9 rounded-full bg-muted" /></div>
      </div>
    ))}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    confirmed: { label: "Confirmed", cls: "bg-primary/10 text-primary" },
    completed: { label: "Completed", cls: "bg-foreground/10 text-foreground" },
    pending_payment: { label: "Pending", cls: "bg-muted text-muted-foreground" },
    pending: { label: "Pending", cls: "bg-muted text-muted-foreground" },
    cancelled: { label: "Cancelled", cls: "bg-foreground/10 text-muted-foreground" },
  };
  const m = map[status] || map.pending;
  return <span className={`rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] ${m.cls}`}>{m.label}</span>;
};

const EmptyState = () => (
  <div className="border border-dashed border-foreground/20 px-6 py-16 text-center md:py-24">
    <p className="font-display text-3xl md:text-4xl">Nothing here yet.</p>
    <p className="mt-3 text-sm text-muted-foreground md:text-base">When you book a stay, it'll appear here.</p>
    <Link to="/properties" className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm text-background">Browse stays <ArrowRight size={16} /></Link>
  </div>
);

const fmtRange = (a, b) => {
  const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return a && b ? `${fmt(a)} – ${fmt(b)}` : "Dates unavailable";
};

export default Bookings;
